import "@/styles/globals.css"

import { useState, type ReactElement, type ReactNode } from "react"
import type { NextPage } from "next"
import NextApp, {
  type AppContext,
  type AppInitialProps,
  type AppProps,
} from "next/app"
import dynamic from "next/dynamic"
import { useRouter } from "next/router"

import { ClerkThemeProvider } from "@/app-shell/components/ClerkThemeProvider"
import { ThemeShortcut } from "@/app-shell/components/ThemeShortcut"
import { AppShellDomainContributionsProvider } from "@/navigation/routes/AppShellDomainContributionsRoute"
import { Toaster, TooltipProvider } from "@coval/ui"
import { HeroUIProvider, ToastProvider } from "@heroui/react"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ThemeProvider as NextThemesProvider } from "next-themes"

import "ag-grid-community/styles/ag-grid.css"
import "ag-grid-community/styles/ag-theme-alpine.css"
import "@/styles/ag-grid-custom.css"
import "@/styles/split-gutter.css"

import { AUTH_PATHS } from "@/constants/auth-routes"

import { DatadogIdentify, PostHogIdentify } from "@/lib/analytics"
import { AppErrorBoundary } from "@/components/error/AppErrorBoundary"
import { CommandRegistryProvider } from "@/contexts/CommandRegistryContext"
import { HeaderActionsProvider } from "@/contexts/HeaderActionsContext"
import { KeyboardShortcutsProvider } from "@/contexts/KeyboardShortcutsContext"
import { PageHeaderProvider } from "@/contexts/PageHeaderContext"

import "@/lib/datadog"

const ReactQueryDevtools = dynamic(
  () =>
    import("@tanstack/react-query-devtools").then(
      (mod) => mod.ReactQueryDevtools,
    ),
  { ssr: false },
)

declare global {
  interface Window {
    pylon?: {
      chat_settings: {
        app_id: string
        email: string
        name: string
        avatar_url: string
        account_id: string
      }
    }
  }
}

export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode
}

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
}

function App({ Component, pageProps }: AppPropsWithLayout) {
  const router = useRouter()
  const [queryClient] = useState(() => new QueryClient())

  // Default: pages have padding unless they use NoPadding layout
  const getLayout = Component.getLayout ?? ((page) => page)

  return (
    <AppErrorBoundary level="app">
      <NextThemesProvider attribute="class" defaultTheme="light">
        <ClerkThemeProvider
          {...pageProps}
          afterSignOutUrl={AUTH_PATHS.SIGN_IN}
          fallbackRedirectUrl="/home"
          signInUrl={AUTH_PATHS.SIGN_IN}
          signUpUrl={AUTH_PATHS.SIGN_UP}
        >
          <QueryClientProvider client={queryClient}>
            {/*
              The app's only TooltipProvider. It sits above
              `AppShellDomainContributionsProvider` so the Covi panel that
              mounts there is inside it, along with every page. The delays it
              applies live in `@coval/ui`'s `Tooltip.tsx`.
            */}
            <TooltipProvider>
              <AppShellDomainContributionsProvider>
                <PostHogIdentify />
                <DatadogIdentify />
                <KeyboardShortcutsProvider>
                  <ThemeShortcut />
                  <CommandRegistryProvider>
                    <PageHeaderProvider>
                      <HeaderActionsProvider>
                        <HeroUIProvider
                          navigate={(path) => {
                            void router.push(path)
                          }}
                        >
                          <ToastProvider placement="bottom-right" />
                          <Toaster />
                          <ReactQueryDevtools />
                          <AppErrorBoundary
                            level="route"
                            resetKey={router.asPath}
                          >
                            {getLayout(<Component {...pageProps} />)}
                          </AppErrorBoundary>
                        </HeroUIProvider>
                      </HeaderActionsProvider>
                    </PageHeaderProvider>
                  </CommandRegistryProvider>
                </KeyboardShortcutsProvider>
              </AppShellDomainContributionsProvider>
            </TooltipProvider>
          </QueryClientProvider>
        </ClerkThemeProvider>
      </NextThemesProvider>
    </AppErrorBoundary>
  )
}

// A strict nonce-based CSP requires every HTML document to be rendered for the
// incoming request. This intentionally opts Pages Router screens out of
// automatic static optimization so the proxy nonce and rendered scripts match.
App.getInitialProps = (appContext: AppContext): Promise<AppInitialProps> =>
  NextApp.getInitialProps(appContext)

export default App
